// @ts-nocheck
import { gql } from "@apollo/client";
import apolloClient from "./withApollo";
import customApolloClient from "./withApolloUsingApi";
import { signOut } from "../../utils/cognito-services";
import { getMockData, shouldUseMockData } from "./mockData";

// eslint-disable-next-line complexity
export const graphQLFetch = async (
  query,
  variables = {},
  queryType = "query",
  state,
  options = {},
  isNewApolloClient = false
) => {
  const { userInfo } = state();
  const { signInUserSession } = userInfo || {};
  const { idToken } = signInUserSession || {};
  const { jwtToken: userAccessToken } = idToken || {};
  
  try {
    const apollo = isNewApolloClient ? customApolloClient : apolloClient;
    let data: any;
    const queryLiteral = gql`
      ${query}
    `;
    if (isNewApolloClient) {
      data = await customApolloClient.mutate({
        mutation: queryLiteral,
        variables,
        context: {},
      });
    } else {
      if (queryType === "query") {
        data = await apolloClient.query({
          query: queryLiteral,
          variables,
          fetchPolicy: options.fetchPolicy || "cache-first",
          context: {
            headers: {
              authorization: userAccessToken ? `Bearer ${userAccessToken}` : "",
            },
          },
        });
      } else {
        data = await apolloClient.mutate({
          mutation: queryLiteral,
          variables,
          context: {
            headers: {
              authorization: userAccessToken ? `Bearer ${userAccessToken}` : "",
            },
          },
        });
        if (options.cacheKeys?.length) {
          options.cacheKeys.forEach((key) => {
            apollo.cache.evict({ id: "ROOT_QUERY", fieldName: key });
          });
          apollo.cache.gc();
        }
      }
    }
    if (data?.errors) {
      throw data;
    }
    return data?.data;
  } catch (error) {
    console.log("GraphQL Error", error);
    
    // In development mode, handle errors differently
    if (process.env.NODE_ENV === 'development') {
      const { networkError } = error || {};
      
      // Check if it's a network connectivity issue (server not running)
      if (networkError?.code === 'ECONNREFUSED' || networkError?.message?.includes('ECONNREFUSED')) {
        console.error('🚨 Cannot connect to GraphQL server');
        console.error('💡 Please check if the backend server is accessible');
        console.info('🔧 Using mock data for development...');
        const mockData = getMockData(query, variables);
        return mockData;
      }
      
      // Check for specific authentication errors
      const { errors } = error || {};
      const isAuthError = Array.isArray(errors) && 
        errors.some(err => 
          err.errorType === "UnauthorizedException" || 
          err.message?.includes('Unauthorized') ||
          err.message?.includes('Authentication')
        );
      
      if (isAuthError) {
        console.log('Authentication error in development mode, redirecting to login');
        window.location.href = "/login";
      } else {
        // For other GraphQL errors in development, provide detailed logging
        console.group('🔍 GraphQL Error Details (Development Mode)');
        console.log('Query:', query);
        console.log('Variables:', variables);
        console.log('Error:', error);
        if (error.graphQLErrors?.length) {
          console.log('GraphQL Errors:', error.graphQLErrors);
        }
        if (error.networkError) {
          console.log('Network Error:', error.networkError);
        }
        console.log('💡 This might indicate:');
        console.log('  • Local GraphQL server schema differences');
        console.log('  • Missing data in local database');
        console.log('  • Local server not implementing this query');
        console.groupEnd();
        
        // Check for specific resolver missing errors (400 status code)
        const isResolverMissing = error.networkError?.statusCode === 400 || 
                                 error.networkError?.response?.status === 400;
        
        if (isResolverMissing && queryType === 'mutation') {
          console.error('🚨 GraphQL Mutation Resolver Missing!');
          console.error('The backend server is missing a resolver for this mutation.');
          console.error('This is a backend configuration issue that needs to be fixed.');
          
          // For CreateBudgetItems specifically, return a mock success in development
          if (query.includes('CreateBudgetItems')) {
            console.info('🎭 Returning mock success for CreateBudgetItems in development...');
            return {
              CreateBudgetItems: {
                success: true,
                message: 'Mock success - Backend resolver missing but items formatted correctly'
              }
            };
          }
        }
        
        // Check if we should use mock data for this error
        if (shouldUseMockData(error)) {
          console.info('🎭 Using mock data due to schema/query mismatch...');
          const mockData = getMockData(query, variables);
          return mockData;
        }
        
        // Return empty data structure that matches expected format
        return { data: null };
      }
    } else {
      // Production error handling
      const errorMessage = `level-capital > graphQLFetch > error > ${JSON.stringify(
        error
      )} > apolloFetch`;
      const { networkError } = error || {};
      const { result } = networkError || {};
      const { errors } = result || {};
      
      if (
        (Array.isArray(errors) &&
          errors.length > 0 &&
          errors[0].errorType === "UnauthorizedException") ||
        error.errors?.length > 0
      ) {
        signOut({ user: userInfo }, () => {
          window.location.href = "/login";
        });
      }
    }
  }
};
